import FeatureLandingPage from "@/components/features/FeatureLandingPage";

const SITE = "https://shattaraai.com";

const howItWorks = [
  { title: "Upload a Lecture", description: "Add a YouTube link, recorded audio file, or uploaded video. Shattara transcribes it automatically." },
  { title: "AI Extracts Key Points", description: "The content is analyzed and broken into chapters with concise summaries for each section." },
  { title: "Review & Study", description: "Read structured summaries, jump to specific chapters, and use the content as a basis for flashcards or quizzes." },
];

const benefits = [
  { title: "Hours of Lectures in Minutes", description: "Get the key points from a 2-hour lecture in a 5-minute read." },
  { title: "Chapter-by-Chapter Breakdown", description: "Lectures are segmented into chapters so you can find exactly what you need." },
  { title: "Works with Video and Audio", description: "Supports YouTube links, uploaded recordings, and audio files." },
  { title: "Feeds Into Other Tools", description: "Use summaries as a source for flashcards, quizzes, and AI tutor conversations." },
  { title: "Searchable Transcripts", description: "Full transcripts are generated alongside summaries so you can search for specific terms." },
];

const faqItems = [
  { question: "What formats does the summarizer support?", answer: "You can upload YouTube links, video files, and audio recordings. Shattara transcribes the content and generates structured summaries." },
  { question: "How accurate are the summaries?", answer: "Summaries are generated from the full transcript of your lecture. The AI identifies key topics and condenses them while preserving important details." },
  { question: "Can I get a full transcript?", answer: "Yes. Shattara generates both a full searchable transcript and a condensed chapter-by-chapter summary." },
  { question: "Can I create flashcards from a lecture summary?", answer: "Yes. Summaries and transcripts can be used as source material for the AI flashcard generator and quiz generator." },
  { question: "Is there a limit on lecture length?", answer: "Free accounts have limits on upload size and duration. Paid plans support longer recordings and more uploads per day." },
];

const relatedFeatures = [
  { name: "AI Tutor", href: "/ai-tutor", description: "Ask follow-up questions about anything in your lecture summaries." },
  { name: "AI Flashcard Generator", href: "/ai-flashcards", description: "Generate flashcards from lecture transcripts and summaries." },
  { name: "AI Quiz Generator", href: "/ai-quiz-generator", description: "Create practice quizzes based on your lecture content." },
  { name: "AI Exam Generator", href: "/ai-exam-generator", description: "Build full exam simulations from your lecture materials." },
  { name: "Best AI Study Tools", href: "/best-ai-study-tools", description: "Compare the top AI-powered study tools for students in 2026." },
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Shattara AI Lecture Summarizer",
    description: "Automatically transcribe and summarize video and audio lectures into structured, chapter-by-chapter summaries.",
    url: `${SITE}/ai-lecture-summarizer`,
    brand: { "@type": "Organization", name: "Shattara AI" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Summarize Lectures with Shattara AI",
    description: "Turn hours of recorded lectures into concise summaries using Shattara AI.",
    step: howItWorks.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.title,
      text: s.description,
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "AI Lecture Summarizer — Condense Hours of Lectures into Minutes",
    description: "Learn how Shattara AI transcribes and summarizes video and audio lectures into structured, searchable summaries with chapter breakdowns.",
    author: { "@type": "Organization", name: "Shattara AI" },
    publisher: { "@type": "Organization", name: "Shattara AI", url: SITE },
    url: `${SITE}/ai-lecture-summarizer`,
  },
];

export default function AILectureSummarizerPage() {
  return (
    <FeatureLandingPage
      seoTitle="AI Lecture Summarizer — Shattara AI"
      seoDescription="Turn lengthy lectures, PDFs, and notes into concise structured summaries in seconds."
      headline="AI Lecture Summarizer — Hours of Content, Minutes to Review"
      subtitle="Upload a video or audio lecture. Shattara transcribes it, breaks it into chapters, and gives you concise summaries you can actually study from."
      howItWorks={howItWorks}
      benefits={benefits}
      faqItems={faqItems}
      relatedFeatures={relatedFeatures}
      structuredData={structuredData}
    />
  );
}
